import { Component, EventEmitter, Input, Output, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { isEmpty, orderBy } from 'lodash';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { debounceTime, first, map, shareReplay, startWith } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { ContainerAttachmentsService } from './container-attachments.service';
import { CoreApiService } from '../../core-api.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { getRoleMatrix, getContainerProjectId } from '../../shared/container.utils';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { getFileClassification, getOriginInfo, OriginInfo } from '../../shared/file.utils';
import { Container } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { replayValue } from '../../shared/observable.utils';
import { Sortable } from '../../shared/sortable';
import { ViewerParameters } from '../../viewers/frame/viewer-frame.model';
import { format, parseISO } from 'date-fns';

interface FileFilters {
  name: string
  created: string
  classification: string
  type: string
}

@Component({
  selector: 'container-attachments',
  styleUrls: ['./container-attachments.component.scss'],
  templateUrl: './container-attachments.component.html',
})
export class ContainerAttachmentsComponent implements OnChanges, OnDestroy {
  @Input() container: Container

  @Output() afterChange = new EventEmitter<void>()

  readonly attachmentColumns: string[] = ['name', 'created', 'classification', 'type', 'actions']
  canUploadAttachments = false
  features = {
    copyByReference: this.configService.isFeatureEnabled('copy_by_reference'),
  }
  filteredAttachments$: Observable<File[]>
  containerAttachments$ = new ReplaySubject<File[]>(1)
  fileViewParameters$: Observable<ViewerParameters>
  readonly filterFields = new FormGroup({
    name: new FormControl(''),
    created: new FormControl(''),
    classification: new FormControl(''),
    type: new FormControl(''),
  })
  filtersActive = false
  readonly sort = new Sortable('name', 'asc')

  private classifications = new WeakMap<File, string>()
  private origins = new WeakMap<File, OriginInfo>()
  private subscriptions = {
    container: Subscription.EMPTY,
    permissions: Subscription.EMPTY,
    filters: Subscription.EMPTY,
  }

  constructor(
    private configService: ConfigService,
    private containerAttachments: ContainerAttachmentsService,
    private coreApi: CoreApiService,
    private projectsService: ProjectsService,
    private profileService: ProfileService,
  ) {
    const filterChanges$: Observable<FileFilters> = combineLatest([
      replayValue(this.filterFields.controls.name),
      replayValue(this.filterFields.controls.created),
      replayValue(this.filterFields.controls.classification),
      replayValue(this.filterFields.controls.type),
    ]).pipe(
      debounceTime(200),
      map(([name, created, classification, type]) => ({ name, created, classification, type })),
    );

    this.filteredAttachments$ = combineLatest([
      this.sort.sortField$,
      filterChanges$,
      this.containerAttachments$,
    ]).pipe(
      map(([sort, filter, attachments]) => {
        this.filtersActive = Object.values(filter).some(filt => !isEmpty(filt));
        const filtered = attachments.filter(attachment => {
          const name = (attachment.name || '').toLowerCase();
          const created = format(parseISO(attachment.created), 'yyyy-MM-dd HH:mm');
          const classification = (this.getFileClassification(attachment)).toLowerCase();
          const type = (attachment.type || '').toLowerCase();

          if (!name.includes(filter.name.toLowerCase())) {
            return false;
          }
          else if (!created.includes(filter.created)) {
            return false;
          }
          else if (!classification.includes(filter.classification.toLowerCase())) {
            return false;
          }
          else if (!type.includes(filter.type.toLowerCase())) {
            return false;
          }
          return true;
        });
        const iterator = (attachment: File): string | string[] => {
          if (sort[0] === 'classification') {
            const attachmentClassification = this.getFileClassification(attachment);
            return attachmentClassification === 'N/A' ? '' : attachmentClassification;
          }
          return (attachment[sort[0]] || '').toLowerCase();
        };
        return orderBy(filtered, iterator, sort[1] as 'asc' | 'desc');
      }),
      shareReplay(1),
    );

    this.fileViewParameters$ = this.filteredAttachments$.pipe(
      startWith([] as File[]),
      map(files => ({
        fileIds: files.map(file => file._id),
      })),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.container) {
      if (Array.isArray(this.container?.files)) {
        this.containerAttachments$.next(this.container.files);
      }
      if (this.container?._id) {
        if (!['files', 'join-origin'].every(key => key in this.container)) {
          this.loadFiles();
        }
        if (changes.container.firstChange || this.container._id !== changes.container.previousValue?._id) {
          this.updatePermissions();
        }
      }
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  private loadFiles(): void {
    this.subscriptions.container.unsubscribe();
    this.subscriptions.container = this.coreApi.containers.get<Container>(this.container._id, { join: 'origin' }).subscribe(resp => {
      this.container['join-origin'] = resp['join-origin'];
      this.container.files = resp.files;
      this.containerAttachments$.next(this.container.files);
      this.resetCaches();
    });
  }

  private resetCaches(): void {
    this.classifications = new WeakMap();
    this.origins = new WeakMap();
  }

  getFileClassification(file: File): string {
    if (!this.classifications.has(file)) {
      this.classifications.set(file, getFileClassification(file));
    }
    return this.classifications.get(file);
  }

  getFileOrigin(file: File): OriginInfo {
    if (!this.origins.has(file)) {
      this.origins.set(file, getOriginInfo(file, this.container['join-origin']));
    }
    return this.origins.get(file);
  }

  fileQuarantined(file: File): boolean {
    return file.virus_scan?.state === 'quarantined';
  }

  createFile(): void {
    this.containerAttachments.createFile(this.container).then(() => {
      this.afterChange.emit();
    });
  }

  uploadAttachment(): Promise<void> {
    this.container.files = this.container.files || [];
    return this.containerAttachments.upload({
      containerId: this.container._id,
      files: this.container.files,
    }).then(() => {
      this.afterChange.emit();
    });
  }

  signalChange(): void {
    this.afterChange.emit();
  }

  private updatePermissions(): void {
    const projectId = getContainerProjectId(this.container);
    this.subscriptions.permissions.unsubscribe();
    if (projectId) {
      this.subscriptions.permissions = this.projectsService.getProjectPermissions(projectId).subscribe(projectPermissions => {
        this.canUploadAttachments = projectPermissions.includes(PermissionAction.FilesCreateUpload) || projectPermissions.includes(PermissionAction.FilesCreateSingle);
      });
    } else {
      // Fall back to roleMatrix if the container doesn't have roles
      this.subscriptions.permissions = this.profileService.getProfile().pipe(first()).subscribe(profile => {
        const roleMatrix = getRoleMatrix(this.container, profile);
        this.canUploadAttachments = roleMatrix[ContainerAccess.ReadWrite];
      });
    }
  }
}
